import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';
import CacheUpdateManager from './CacheUpdateManager';
import VersionConfigManager from './VersionConfigManager';
import NotificationSettingsManager from './NotificationSettingsManager';
import MapManagement from './MapManagement';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Row Component
const SortableRow = ({ beach, editingBeach, beachForm, handleBeachFormChange, handleToggleBeachActive, handleSaveBeach, handleCancelEditBeach, handleEditBeach, handleDeleteBeach }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: beach.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr 
      ref={setNodeRef} 
      style={style} 
      className={isDragging ? 'dragging' : ''}
    >
      <td>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div
            {...attributes}
            {...listeners}
            style={{ 
              cursor: 'grab', 
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              color: '#666'
            }}
            title="Drag to reorder"
          >
            ⋮⋮
          </div>
          {editingBeach === beach.id ? (
            <input
              type="text"
              className="form-control-sm"
              value={beachForm.name}
              onChange={(e) => handleBeachFormChange('name', e.target.value)}
              required
            />
          ) : (
            beach.name
          )}
        </div>
      </td>
      <td>
        {editingBeach === beach.id ? (
          <textarea
            className="form-control-sm"
            value={beachForm.description}
            onChange={(e) => handleBeachFormChange('description', e.target.value)}
            rows="2"
            style={{ width: '200px' }}
          />
        ) : (
          beach.description || '-'
        )}
      </td>
      <td>
        {editingBeach === beach.id ? (
          <input
            type="number"
            className="form-control-sm"
            value={beachForm.max_people}
            onChange={(e) => handleBeachFormChange('max_people', parseInt(e.target.value) || 3)}
            min="1"
            max="20"
            style={{ width: '80px' }}
          />
        ) : (
          beach.max_people || 3
        )}
      </td>
      <td>
        <span 
          className={`badge ${beach.is_active ? 'badge-success' : 'badge-secondary'}`}
          onClick={() => handleToggleBeachActive(beach.id, beach.is_active)}
          style={{ cursor: 'pointer', userSelect: 'none' }}
          title="Click to toggle active status"
        >
          {beach.is_active ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td>
        {new Date(beach.created_at).toLocaleDateString()}
      </td>
      <td>
        {editingBeach === beach.id ? (
          <div className="btn-group">
            <button
              onClick={() => handleSaveBeach(beach.id)}
              className="btn btn-sm btn-success"
            >
              Save
            </button>
            <button
              onClick={handleCancelEditBeach}
              className="btn btn-sm btn-secondary"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="btn-group">
            <button
              onClick={() => handleEditBeach(beach)}
              className="btn btn-sm btn-primary"
            >
              Edit
            </button>
            <button
              onClick={() => handleDeleteBeach(beach.id, beach.name)}
              className="btn btn-sm btn-danger"
            >
              Delete
            </button>
          </div>
        )}
      </td>
    </tr>
  );
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [beaches, setBeaches] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [ranks, setRanks] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [beachesLoading, setBeachesLoading] = useState(true);
  const [vehiclesLoading, setVehiclesLoading] = useState(true);
  const [ranksLoading, setRanksLoading] = useState(true);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [clearingRequests, setClearingRequests] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBeachForm, setShowBeachForm] = useState(false);
  const [showVehicleForm, setShowVehicleForm] = useState(false);
  const [showRankForm, setShowRankForm] = useState(false);
  const [editingBeach, setEditingBeach] = useState(null);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [editingRank, setEditingRank] = useState(null);
  const [selectedRankForPermissions, setSelectedRankForPermissions] = useState(null);
  const [rankPermissions, setRankPermissions] = useState([]);
  const [beachForm, setBeachForm] = useState({ name: '', description: '', max_people: 3 });
  const [vehicleForm, setVehicleForm] = useState({ name: '', description: '' });
  const [rankForm, setRankForm] = useState({ name: '', display_name: '', priority: 0, description: '' });
  const [addForm, setAddForm] = useState({
    username: '',
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    rank: 'Lifeguard',
    beach: '',
    years_of_service: 0,
    emt_training: false,
    advanced_training: false,
    assigned_vehicle: ''
  });

  const { user } = useAuth();

  // Utility function to safely format user names
  const formatUserName = (userItem) => {
    if (userItem.first_name && userItem.last_name) {
      const firstName = String(userItem.first_name).trim();
      const lastName = String(userItem.last_name).trim();
      return `${firstName} ${lastName}`;
    }
    return userItem.username || 'Unknown User';
  };

  const fetchUsers = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/users');
      console.log('Fetched users data:', response.data);
  
      response.data.forEach((user, index) => {
        console.log(`User ${index}:`, {
          first_name: user.first_name,
          last_name: user.last_name,
          username: user.username,
          first_name_length: user.first_name?.length,
          last_name_length: user.last_name?.length
        });
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      showMessage('Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBeaches = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/beaches');
      console.log('Fetched beaches data:', response.data);
      setBeaches(response.data);
    } catch (error) {
      console.error('Failed to fetch beaches:', error);
      showMessage('Failed to load beaches', 'error');
    } finally {
      setBeachesLoading(false);
    }
  }, []);

  const fetchVehicles = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/vehicles');
      console.log('Fetched vehicles data:', response.data);
      setVehicles(response.data);
    } catch (error) {
      console.error('Failed to fetch vehicles:', error);
      showMessage('Failed to load vehicles', 'error');
    } finally {
      setVehiclesLoading(false);
    }
  }, []);

  const fetchUserPermissions = useCallback(async () => {
    try {
      console.log('🔄 Fetching user permissions...');
      const response = await apiClient.get('/api/user/permissions');
      console.log('📊 API Response for user permissions:', response.data);
      console.log('📊 Response type:', typeof response.data);
      console.log('📊 Is Array:', Array.isArray(response.data));
      setUserPermissions(response.data);
    } catch (error) {
      console.error('❌ Failed to fetch user permissions:', error);
      console.error('❌ Error details:', error.response?.data);
    }
  }, []);

  const fetchRanks = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/ranks');
      setRanks(response.data);
    } catch (error) {
      console.error('Failed to fetch ranks:', error);
      showMessage('Failed to load ranks', 'error');
    } finally {
      setRanksLoading(false);
    }
  }, []);

  const fetchPermissions = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/permissions');
      setPermissions(response.data);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      showMessage('Failed to load permissions', 'error');
    } finally {
      setPermissionsLoading(false);
    }
  }, []);

  const fetchRankPermissions = useCallback(async (rankId) => {
    try {
      const response = await apiClient.get(`/api/ranks/${rankId}/permissions`);
      setRankPermissions(response.data);
    } catch (error) {
      console.error('Failed to fetch rank permissions:', error);
      showMessage('Failed to load rank permissions', 'error');
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchBeaches();
    fetchVehicles();
    fetchUserPermissions();
  }, [fetchUsers, fetchBeaches, fetchVehicles, fetchUserPermissions]);

  useEffect(() => {
    console.log('🔐 User permissions updated:', userPermissions);
    if (userPermissions.includes('manage_ranks')) {
      console.log('✅ User has manage_ranks permission, fetching ranks...');
      fetchRanks();
    } else {
      console.log('❌ User does not have manage_ranks permission');
    }
    if (userPermissions.includes('manage_permissions')) {
      console.log('✅ User has manage_permissions permission, fetching permissions...');
      fetchPermissions();
    } else {
      console.log('❌ User does not have manage_permissions permission');
    }
  }, [userPermissions, fetchRanks, fetchPermissions]);

  const showMessage = (text, type) => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  const handleEditClick = (userToEdit) => {
    setEditingUser(userToEdit.id);
    setEditForm({
      username: userToEdit.username,
      first_name: userToEdit.first_name || '',
      last_name: userToEdit.last_name || '',
      email: userToEdit.email || '',
      rank: userToEdit.rank,
      beach: userToEdit.beach || '',
      years_of_service: userToEdit.years_of_service,
      emt_training: userToEdit.emt_training,
      advanced_training: userToEdit.advanced_training,
      assigned_vehicle: userToEdit.assigned_vehicle || ''
    });
  };

  const handleEditSave = async (userId) => {
    try {
      await apiClient.put(`/api/users/${userId}`, editForm);
      await fetchUsers();
      setEditingUser(null);
      showMessage('User updated successfully', 'success');
    } catch (error) {
      console.error('Failed to update user:', error);
      showMessage(
        error.response?.data?.error || 'Failed to update user',
        'error'
      );
    }
  };

  const handleEditCancel = () => {
    setEditingUser(null);
    setEditForm({});
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"?`)) {
      return;
    }

    try {
      await apiClient.delete(`/api/users/${userId}`);
      await fetchUsers();
      showMessage('User deleted successfully', 'success');
    } catch (error) {
      console.error('Failed to delete user:', error);
      showMessage(
        error.response?.data?.error || 'Failed to delete user',
        'error'
      );
    }
  };

  const handleClearScheduleRequests = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to clear all future schedule requests for ${userName}? This action cannot be undone.`)) {
      return;
    }

    setClearingRequests(true);
    try {
      const response = await apiClient.delete(`/api/schedule-requests/clear/${userId}`);
      showMessage(response.data.message, 'success');
    } catch (error) {
      console.error('Failed to clear schedule requests:', error);
      showMessage(
        error.response?.data?.error || 'Failed to clear schedule requests',
        'error'
      );
    } finally {
      setClearingRequests(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    
    try {
      await apiClient.post('/api/register', addForm);
      await fetchUsers();
      setShowAddForm(false);
      setAddForm({
        username: '',
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        rank: 'Lifeguard',
        beach: '',
        years_of_service: 0,
        emt_training: false,
        advanced_training: false,
        assigned_vehicle: ''
      });
      showMessage('User added successfully', 'success');
    } catch (error) {
      console.error('Failed to add user:', error);
      showMessage(
        error.response?.data?.error || 'Failed to add user',
        'error'
      );
    }
  };

  const canModifyUser = (targetUser) => {
    const rankHierarchy = {
      'Captain': 5,
      'Lieutenant': 4,
      'Sergeant': 3,
      'Lifeguard': 2,
      'Rookie': 1
    };

    const currentUserRank = rankHierarchy[user.rank];
    const targetUserRank = rankHierarchy[targetUser.rank];

    return currentUserRank > targetUserRank || user.id === targetUser.id;
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddFormChange = (field, value) => {
    setAddForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Beach Management Functions
  const handleAddBeach = async (e) => {
    e.preventDefault();
    
    try {
      await apiClient.post('/api/beaches', beachForm);
      await fetchBeaches();
      setShowBeachForm(false);
      setBeachForm({ name: '', description: '', max_people: 3 });
      showMessage('Beach added successfully', 'success');
    } catch (error) {
      console.error('Failed to add beach:', error);
      showMessage(
        error.response?.data?.error || 'Failed to add beach',
        'error'
      );
    }
  };

  const handleEditBeach = (beach) => {
    setEditingBeach(beach.id);
    setBeachForm({
      name: beach.name,
      description: beach.description || '',
      max_people: beach.max_people || 3
    });
  };

  const handleSaveBeach = async (beachId) => {
    try {
      await apiClient.put(`/api/beaches/${beachId}`, beachForm);
      await fetchBeaches();
      setEditingBeach(null);
      setBeachForm({ name: '', description: '' });
      showMessage('Beach updated successfully', 'success');
    } catch (error) {
      console.error('Failed to update beach:', error);
      showMessage(
        error.response?.data?.error || 'Failed to update beach',
        'error'
      );
    }
  };

  const handleCancelEditBeach = () => {
    setEditingBeach(null);
    setBeachForm({ name: '', description: '', max_people: 3 });
  };

  const handleDeleteBeach = async (beachId, beachName) => {
    if (!window.confirm(`Are you sure you want to delete beach "${beachName}"?`)) {
      return;
    }

    try {
      await apiClient.delete(`/api/beaches/${beachId}`);
      await fetchBeaches();
      showMessage('Beach deleted successfully', 'success');
    } catch (error) {
      console.error('Failed to delete beach:', error);
      showMessage(
        error.response?.data?.error || 'Failed to delete beach',
        'error'
      );
    }
  };

  const handleBeachFormChange = (field, value) => {
    setBeachForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Vehicle Management Functions
  const handleAddVehicle = async (e) => {
    e.preventDefault();
    
    try {
      await apiClient.post('/api/vehicles', vehicleForm);
      await fetchVehicles();
      setShowVehicleForm(false);
      setVehicleForm({ name: '', description: '' });
      showMessage('Vehicle added successfully', 'success');
    } catch (error) {
      console.error('Failed to add vehicle:', error);
      showMessage(
        error.response?.data?.error || 'Failed to add vehicle',
        'error'
      );
    }
  };

  const handleEditVehicle = (vehicle) => {
    setEditingVehicle(vehicle.id);
    setVehicleForm({
      name: vehicle.name,
      description: vehicle.description || ''
    });
  };

  const handleSaveVehicle = async (vehicleId) => {
    try {
      await apiClient.put(`/api/vehicles/${vehicleId}`, vehicleForm);
      await fetchVehicles();
      setEditingVehicle(null);
      setVehicleForm({ name: '', description: '' });
      showMessage('Vehicle updated successfully', 'success');
    } catch (error) {
      console.error('Failed to update vehicle:', error);
      showMessage(
        error.response?.data?.error || 'Failed to update vehicle',
        'error'
      );
    }
  };

  const handleCancelEditVehicle = () => {
    setEditingVehicle(null);
    setVehicleForm({ name: '', description: '' });
  };

  const handleDeleteVehicle = async (vehicleId, vehicleName) => {
    if (!window.confirm(`Are you sure you want to delete vehicle "${vehicleName}"?`)) {
      return;
    }

    try {
      await apiClient.delete(`/api/vehicles/${vehicleId}`);
      await fetchVehicles();
      showMessage('Vehicle deleted successfully', 'success');
    } catch (error) {
      console.error('Failed to delete vehicle:', error);
      showMessage(
        error.response?.data?.error || 'Failed to delete vehicle',
        'error'
      );
    }
  };

  const handleVehicleFormChange = (field, value) => {
    setVehicleForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Rank Management Functions
  const handleAddRank = async (e) => {
    e.preventDefault();
    
    try {
      await apiClient.post('/api/ranks', rankForm);
      await fetchRanks();
      setShowRankForm(false);
      setRankForm({ name: '', display_name: '', priority: 0, description: '' });
      showMessage('Rank added successfully', 'success');
    } catch (error) {
      console.error('Failed to add rank:', error);
      showMessage(
        error.response?.data?.error || 'Failed to add rank',
        'error'
      );
    }
  };

  const handleEditRank = (rank) => {
    setEditingRank(rank.id);
    setRankForm({
      name: rank.name,
      display_name: rank.display_name,
      priority: rank.priority,
      description: rank.description || ''
    });
  };

  const handleSaveRank = async (rankId) => {
    try {
      await apiClient.put(`/api/ranks/${rankId}`, rankForm);
      await fetchRanks();
      setEditingRank(null);
      setRankForm({ name: '', display_name: '', priority: 0, description: '' });
      showMessage('Rank updated successfully', 'success');
    } catch (error) {
      console.error('Failed to update rank:', error);
      showMessage(
        error.response?.data?.error || 'Failed to update rank',
        'error'
      );
    }
  };

  const handleCancelEditRank = () => {
    setEditingRank(null);
    setRankForm({ name: '', display_name: '', priority: 0, description: '' });
  };

  const handleDeleteRank = async (rankId, rankName) => {
    if (!window.confirm(`Are you sure you want to delete rank "${rankName}"?`)) {
      return;
    }

    try {
      await apiClient.delete(`/api/ranks/${rankId}`);
      await fetchRanks();
      showMessage('Rank deleted successfully', 'success');
    } catch (error) {
      console.error('Failed to delete rank:', error);
      showMessage(
        error.response?.data?.error || 'Failed to delete rank',
        'error'
      );
    }
  };

  const handleRankFormChange = (field, value) => {
    setRankForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Permission Management Functions
  const handleSelectRankForPermissions = async (rank) => {
    setSelectedRankForPermissions(rank);
    await fetchRankPermissions(rank.id);
  };

  const handlePermissionToggle = (permissionId) => {
    setRankPermissions(prev => 
      prev.map(perm => 
        perm.id === permissionId 
          ? { ...perm, has_permission: !perm.has_permission }
          : perm
      )
    );
  };

  const handleSaveRankPermissions = async () => {
    if (!selectedRankForPermissions) return;

    try {
      const permissionIds = rankPermissions
        .filter(perm => perm.has_permission)
        .map(perm => perm.id);

      await apiClient.put(`/api/ranks/${selectedRankForPermissions.id}/permissions`, {
        permissionIds
      });

      showMessage('Rank permissions updated successfully', 'success');
    } catch (error) {
      console.error('Failed to update rank permissions:', error);
      showMessage(
        error.response?.data?.error || 'Failed to update rank permissions',
        'error'
      );
    }
  };

  const handleToggleVehicleActive = async (vehicleId, currentStatus) => {
    try {
      await apiClient.put(`/api/vehicles/${vehicleId}`, {
        is_active: !currentStatus
      });
      await fetchVehicles();
      showMessage(
        `Vehicle ${!currentStatus ? 'activated' : 'deactivated'} successfully`,
        'success'
      );
    } catch (error) {
      console.error('Failed to toggle vehicle status:', error);
      showMessage('Failed to update vehicle status', 'error');
    }
  };

  // New function to toggle beach active status
  const handleToggleBeachActive = async (beachId, currentStatus) => {
    try {
      await apiClient.put(`/api/beaches/${beachId}`, {
        is_active: !currentStatus
      });
      await fetchBeaches();
      showMessage(
        `Beach ${!currentStatus ? 'activated' : 'deactivated'} successfully`, 
        'success'
      );
    } catch (error) {
      console.error('Failed to toggle beach status:', error);
      showMessage(
        error.response?.data?.error || 'Failed to update beach status',
        'error'
      );
    }
  };

  // Filter active beaches for dropdowns
  const activeBeaches = beaches.filter(beach => beach.is_active);

  // New function to handle beach reordering
  const handleBeachReorder = async (newBeaches) => {
    try {
      const beachOrders = newBeaches.map((beach, index) => ({
        id: beach.id,
        sort_order: index + 1
      }));

      console.log('🔄 Attempting to reorder beaches:', beachOrders);
      
      const response = await apiClient.put('/api/beaches/reorder', { beachOrders });
      console.log('✅ Beach reorder response:', response.data);
      
      showMessage('Beach order updated successfully', 'success');
    } catch (error) {
      console.error('❌ Failed to update beach order:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      showMessage(
        error.response?.data?.error || 'Failed to update beach order',
        'error'
      );
      // Revert the change by fetching fresh data
      await fetchBeaches();
    }
  };

  // Drag and drop handlers
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = beaches.findIndex((beach) => beach.id === active.id);
      const newIndex = beaches.findIndex((beach) => beach.id === over.id);
      
      const newBeaches = arrayMove(beaches, oldIndex, newIndex);
      setBeaches(newBeaches);
      
      // Update the order in the database
      handleBeachReorder(newBeaches);
    }
  };

  if (loading) {
    return (
      <div className="user-management-full-width">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  // Debug completed - rank and permission management sections working correctly

  return (
    <div className="user-management-full-width">
      <div className="page-header">
        <h1 className="page-title">Management</h1>
        <p className="page-subtitle">Manage beach patrol personnel</p>
      </div>

      {message && (
        <div className={`alert alert-${messageType}`}>
          {message}
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">All Users ({users.length})</h2>
          <button
            onClick={() => setShowAddForm(true)}
            className="btn btn-primary"
          >
            Add New User
          </button>
        </div>

        {showAddForm && (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Add New User</h3>
            </div>
            <form onSubmit={handleAddUser} className="form">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">First Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={addForm.first_name}
                    onChange={(e) => handleAddFormChange('first_name', e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Last Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={addForm.last_name}
                    onChange={(e) => handleAddFormChange('last_name', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Email Address</label>
                  <input
                    type="email"
                    className="form-control"
                    value={addForm.email}
                    onChange={(e) => handleAddFormChange('email', e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Username</label>
                  <input
                    type="text"
                    className="form-control"
                    value={addForm.username}
                    onChange={(e) => handleAddFormChange('username', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Password</label>
                  <input
                    type="password"
                    className="form-control"
                    value={addForm.password}
                    onChange={(e) => handleAddFormChange('password', e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Rank</label>
                  <select
                    className="form-control"
                    value={addForm.rank}
                    onChange={(e) => handleAddFormChange('rank', e.target.value)}
                    required
                  >
                    <option value="Rookie">Rookie</option>
                    <option value="Lifeguard">Lifeguard</option>
                    <option value="Sergeant">Sergeant</option>
                    <option value="Lieutenant">Lieutenant</option>
                    <option value="Captain">Captain</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Beach Assignment</label>
                  <select
                    className="form-control"
                    value={addForm.beach}
                    onChange={(e) => handleAddFormChange('beach', e.target.value)}
                  >
                    <option value="">Select Beach</option>
                    {activeBeaches.map((beach) => (
                      <option key={beach.id} value={beach.name}>
                        {beach.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Years of Service</label>
                  <input
                    type="number"
                    className="form-control"
                    value={addForm.years_of_service}
                    onChange={(e) => handleAddFormChange('years_of_service', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Assigned Vehicle</label>
                  <select
                    className="form-control"
                    value={addForm.assigned_vehicle}
                    onChange={(e) => handleAddFormChange('assigned_vehicle', e.target.value)}
                  >
                    <option value="">Select Vehicle</option>
                    {vehicles.filter(vehicle => vehicle.is_active).map((vehicle) => (
                      <option key={vehicle.id} value={vehicle.name}>
                        {vehicle.name} {vehicle.description && `- ${vehicle.description}`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={addForm.emt_training}
                      onChange={(e) => handleAddFormChange('emt_training', e.target.checked)}
                    />
                    EMT Training
                  </label>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={addForm.advanced_training}
                      onChange={(e) => handleAddFormChange('advanced_training', e.target.checked)}
                    />
                    Advanced Training
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  Add User
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Username</th>
                <th>Email</th>
                <th>Rank</th>
                <th>Beach</th>
                <th>Years</th>
                <th>EMT</th>
                <th>Advanced</th>
                <th>Vehicle</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((userItem) => (
                <tr key={userItem.id}>
                  <td>
                    {editingUser === userItem.id ? (
                      <div style={{ display: 'flex', gap: '4px', flexDirection: 'column' }}>
                        <input
                          type="text"
                          className="form-control-sm"
                          value={editForm.first_name}
                          onChange={(e) => handleEditFormChange('first_name', e.target.value)}
                          placeholder="First Name"
                          style={{ marginBottom: '2px' }}
                        />
                        <input
                          type="text"
                          className="form-control-sm"
                          value={editForm.last_name}
                          onChange={(e) => handleEditFormChange('last_name', e.target.value)}
                          placeholder="Last Name"
                        />
                      </div>
                    ) : (
                      <div>
                        {formatUserName(userItem)}
                      </div>
                    )}
                  </td>
                  <td>
                    {editingUser === userItem.id ? (
                      <input
                        type="text"
                        className="form-control-sm"
                        value={editForm.username}
                        onChange={(e) => handleEditFormChange('username', e.target.value)}
                      />
                    ) : (
                      userItem.username
                    )}
                  </td>
                  <td>
                    {editingUser === userItem.id ? (
                      <input
                        type="email"
                        className="form-control-sm"
                        value={editForm.email}
                        onChange={(e) => handleEditFormChange('email', e.target.value)}
                      />
                    ) : (
                      userItem.email || '-'
                    )}
                  </td>
                  <td>
                    {editingUser === userItem.id ? (
                      <select
                        className="form-control-sm"
                        value={editForm.rank}
                        onChange={(e) => handleEditFormChange('rank', e.target.value)}
                      >
                        <option value="Rookie">Rookie</option>
                        <option value="Lifeguard">Lifeguard</option>
                        <option value="Sergeant">Sergeant</option>
                        <option value="Lieutenant">Lieutenant</option>
                        <option value="Captain">Captain</option>
                      </select>
                    ) : (
                      <span className={`badge badge-rank badge-${userItem.rank.toLowerCase()}`}>
                        {userItem.rank}
                      </span>
                    )}
                  </td>
                  <td>
                    {editingUser === userItem.id ? (
                      <select
                        className="form-control-sm"
                        value={editForm.beach}
                        onChange={(e) => handleEditFormChange('beach', e.target.value)}
                      >
                        <option value="">Select Beach</option>
                        {activeBeaches.map((beach) => (
                          <option key={beach.id} value={beach.name}>
                            {beach.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      userItem.beach || '-'
                    )}
                  </td>
                  <td>
                    {editingUser === userItem.id ? (
                      <input
                        type="number"
                        className="form-control-sm"
                        value={editForm.years_of_service}
                        onChange={(e) => handleEditFormChange('years_of_service', parseInt(e.target.value) || 0)}
                        min="0"
                        style={{ width: '70px' }}
                      />
                    ) : (
                      userItem.years_of_service
                    )}
                  </td>
                  <td>
                    {editingUser === userItem.id ? (
                      <input
                        type="checkbox"
                        checked={editForm.emt_training}
                        onChange={(e) => handleEditFormChange('emt_training', e.target.checked)}
                      />
                    ) : (
                      <span className={`badge ${userItem.emt_training ? 'badge-success' : 'badge-secondary'}`}>
                        {userItem.emt_training ? 'Yes' : 'No'}
                      </span>
                    )}
                  </td>
                  <td>
                    {editingUser === userItem.id ? (
                      <input
                        type="checkbox"
                        checked={editForm.advanced_training}
                        onChange={(e) => handleEditFormChange('advanced_training', e.target.checked)}
                      />
                    ) : (
                      <span className={`badge ${userItem.advanced_training ? 'badge-success' : 'badge-secondary'}`}>
                        {userItem.advanced_training ? 'Yes' : 'No'}
                      </span>
                    )}
                  </td>
                  <td>
                    {editingUser === userItem.id ? (
                      <select
                        className="form-control-sm"
                        value={editForm.assigned_vehicle}
                        onChange={(e) => handleEditFormChange('assigned_vehicle', e.target.value)}
                        style={{ width: '120px' }}
                      >
                        <option value="">Select Vehicle</option>
                        {vehicles.filter(vehicle => vehicle.is_active).map((vehicle) => (
                          <option key={vehicle.id} value={vehicle.name}>
                            {vehicle.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      userItem.assigned_vehicle || '-'
                    )}
                  </td>
                  <td>
                    {editingUser === userItem.id ? (
                      <div className="btn-group">
                        <button
                          onClick={() => handleEditSave(userItem.id)}
                          className="btn btn-sm btn-success"
                        >
                          Save
                        </button>
                        <button
                          onClick={handleEditCancel}
                          className="btn btn-sm btn-secondary"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="btn-group">
                        {canModifyUser(userItem) && (
                          <>
                            <button
                              onClick={() => handleEditClick(userItem)}
                              className="btn btn-sm btn-primary"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteUser(userItem.id, 
                                formatUserName(userItem)
                              )}
                              className="btn btn-sm btn-danger"
                            >
                              Delete
                            </button>
                            {/* Show Clear Schedule Requests button only for admins (Captain, Lieutenant, Sergeant) */}
                            {['Captain', 'Lieutenant', 'Sergeant'].includes(user.rank) && (
                              <button
                                onClick={() => handleClearScheduleRequests(userItem.id, 
                                  formatUserName(userItem)
                                )}
                                className="btn btn-sm btn-warning"
                                disabled={clearingRequests}
                                title="Clear all future schedule requests for this user"
                              >
                                {clearingRequests ? 'Clearing...' : 'Clear Schedule Requests'}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Beach Management Section */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Beach Management ({beaches.length})</h2>
          <button
            onClick={() => setShowBeachForm(true)}
            className="btn btn-primary"
          >
            Add New Beach
          </button>
        </div>

        {showBeachForm && (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Add New Beach</h3>
            </div>
            <form onSubmit={handleAddBeach} className="form">
              <div className="form-group">
                <label className="form-label">Beach Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={beachForm.name}
                  onChange={(e) => handleBeachFormChange('name', e.target.value)}
                  required
                  placeholder="e.g., Sunset Beach"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Description (Optional)</label>
                <textarea
                  className="form-control"
                  value={beachForm.description}
                  onChange={(e) => handleBeachFormChange('description', e.target.value)}
                  placeholder="Optional description for the beach"
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Maximum People</label>
                <input
                  type="number"
                  className="form-control"
                  value={beachForm.max_people}
                  onChange={(e) => handleBeachFormChange('max_people', parseInt(e.target.value) || 3)}
                  min="1"
                  max="20"
                  placeholder="3"
                />
                <small className="form-text text-muted">Maximum number of people allowed at this beach</small>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  Add Beach
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowBeachForm(false);
                    setBeachForm({ name: '', description: '', max_people: 3 });
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="table-container">
          <DndContext 
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <table className="table">
              <thead>
                <tr>
                  <th>Beach Name</th>
                  <th>Description</th>
                  <th>Max People</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {beachesLoading ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                      Loading beaches...
                    </td>
                  </tr>
                ) : beaches.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                      No beaches found
                    </td>
                  </tr>
                ) : (
                  <SortableContext 
                    items={beaches.map(beach => beach.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {beaches.map((beach) => (
                      <SortableRow
                        key={beach.id}
                        beach={beach}
                        editingBeach={editingBeach}
                        beachForm={beachForm}
                        handleBeachFormChange={handleBeachFormChange}
                        handleToggleBeachActive={handleToggleBeachActive}
                        handleSaveBeach={handleSaveBeach}
                        handleCancelEditBeach={handleCancelEditBeach}
                        handleEditBeach={handleEditBeach}
                        handleDeleteBeach={handleDeleteBeach}
                      />
                    ))}
                  </SortableContext>
                )}
              </tbody>
            </table>
          </DndContext>
        </div>
      </div>

      {/* Vehicle Management Section */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Vehicle Management ({vehicles.length})</h2>
          <button
            onClick={() => setShowVehicleForm(true)}
            className="btn btn-primary"
          >
            Add New Vehicle
          </button>
        </div>

        {showVehicleForm && (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Add New Vehicle</h3>
            </div>
            <form onSubmit={handleAddVehicle} className="form">
              <div className="form-group">
                <label className="form-label">Vehicle Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={vehicleForm.name}
                  onChange={(e) => handleVehicleFormChange('name', e.target.value)}
                  required
                  placeholder="e.g., ATV-001, TRUCK-001"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Description (Optional)</label>
                <textarea
                  className="form-control"
                  value={vehicleForm.description}
                  onChange={(e) => handleVehicleFormChange('description', e.target.value)}
                  placeholder="Optional description for the vehicle"
                  rows="3"
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  Add Vehicle
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowVehicleForm(false);
                    setVehicleForm({ name: '', description: '' });
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Vehicle Name</th>
                <th>Description</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {vehiclesLoading ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                    Loading vehicles...
                  </td>
                </tr>
              ) : vehicles.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                    No vehicles found
                  </td>
                </tr>
              ) : (
                vehicles.map((vehicle) => (
                  <tr key={vehicle.id}>
                    <td>
                      {editingVehicle === vehicle.id ? (
                        <input
                          type="text"
                          className="form-control-sm"
                          value={vehicleForm.name}
                          onChange={(e) => handleVehicleFormChange('name', e.target.value)}
                          required
                        />
                      ) : (
                        vehicle.name
                      )}
                    </td>
                    <td>
                      {editingVehicle === vehicle.id ? (
                        <textarea
                          className="form-control-sm"
                          value={vehicleForm.description}
                          onChange={(e) => handleVehicleFormChange('description', e.target.value)}
                          rows="2"
                          style={{ width: '200px' }}
                        />
                      ) : (
                        vehicle.description || '-'
                      )}
                    </td>
                    <td>
                      <span 
                        className={`badge ${vehicle.is_active ? 'badge-success' : 'badge-secondary'}`}
                        onClick={() => handleToggleVehicleActive(vehicle.id, vehicle.is_active)}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        title="Click to toggle active status"
                      >
                        {vehicle.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      {new Date(vehicle.created_at).toLocaleDateString()}
                    </td>
                    <td>
                      {editingVehicle === vehicle.id ? (
                        <div className="btn-group">
                          <button
                            onClick={() => handleSaveVehicle(vehicle.id)}
                            className="btn btn-sm btn-success"
                          >
                            Save
                          </button>
                          <button
                            onClick={handleCancelEditVehicle}
                            className="btn btn-sm btn-secondary"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="btn-group">
                          <button
                            onClick={() => handleEditVehicle(vehicle)}
                            className="btn btn-sm btn-primary"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteVehicle(vehicle.id, vehicle.name)}
                            className="btn btn-sm btn-danger"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Complex Rank Management Section - TESTING */}
      {userPermissions.includes('manage_ranks') && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Rank Management ({ranks.length})</h2>
            <button
              onClick={() => setShowRankForm(true)}
              className="btn btn-primary"
            >
              Add New Rank
            </button>
          </div>

          {showRankForm && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Add New Rank</h3>
              </div>
              <form onSubmit={handleAddRank} className="form">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Rank Name (Internal)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={rankForm.name}
                      onChange={(e) => handleRankFormChange('name', e.target.value)}
                      required
                      placeholder="e.g., senior_lifeguard"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Display Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={rankForm.display_name}
                      onChange={(e) => handleRankFormChange('display_name', e.target.value)}
                      required
                      placeholder="e.g., Senior Lifeguard"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Priority Level</label>
                    <input
                      type="number"
                      className="form-control"
                      value={rankForm.priority}
                      onChange={(e) => handleRankFormChange('priority', parseInt(e.target.value) || 0)}
                      required
                      min="0"
                      placeholder="Higher number = higher priority"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      value={rankForm.description}
                      onChange={(e) => handleRankFormChange('description', e.target.value)}
                      placeholder="Optional description of the rank"
                      rows="2"
                    />
                  </div>
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">
                    Add Rank
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowRankForm(false);
                      setRankForm({ name: '', display_name: '', priority: 0, description: '' });
                    }}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Display Name</th>
                  <th>Internal Name</th>
                  <th>Priority</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {ranksLoading ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                      Loading ranks...
                    </td>
                  </tr>
                ) : ranks.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                      No ranks found
                    </td>
                  </tr>
                ) : (
                  ranks.map((rank) => (
                    <tr key={rank.id}>
                      <td>
                        {editingRank === rank.id ? (
                          <input
                            type="text"
                            className="form-control-sm"
                            value={rankForm.display_name}
                            onChange={(e) => handleRankFormChange('display_name', e.target.value)}
                            required
                          />
                        ) : (
                          rank.display_name
                        )}
                      </td>
                      <td>
                        {editingRank === rank.id ? (
                          <input
                            type="text"
                            className="form-control-sm"
                            value={rankForm.name}
                            onChange={(e) => handleRankFormChange('name', e.target.value)}
                            required
                          />
                        ) : (
                          rank.name
                        )}
                      </td>
                      <td>
                        {editingRank === rank.id ? (
                          <input
                            type="number"
                            className="form-control-sm"
                            value={rankForm.priority}
                            onChange={(e) => handleRankFormChange('priority', parseInt(e.target.value) || 0)}
                            min="0"
                          />
                        ) : (
                          rank.priority
                        )}
                      </td>
                      <td>
                        {editingRank === rank.id ? (
                          <textarea
                            className="form-control-sm"
                            value={rankForm.description}
                            onChange={(e) => handleRankFormChange('description', e.target.value)}
                            rows="2"
                            style={{ width: '200px' }}
                          />
                        ) : (
                          rank.description || '-'
                        )}
                      </td>
                      <td>
                        <span className={`badge ${rank.is_active ? 'badge-success' : 'badge-secondary'}`}>
                          {rank.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        {editingRank === rank.id ? (
                          <div className="btn-group">
                            <button
                              onClick={() => handleSaveRank(rank.id)}
                              className="btn btn-sm btn-success"
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancelEditRank}
                              className="btn btn-sm btn-secondary"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="btn-group">
                            <button
                              onClick={() => handleEditRank(rank)}
                              className="btn btn-sm btn-primary"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteRank(rank.id, rank.display_name)}
                              className="btn btn-sm btn-danger"
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Complex Permission Management Section - TESTING */}
      {userPermissions.includes('manage_permissions') && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Permission Management</h2>
          </div>

          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label className="form-label">Select Rank to Manage Permissions:</label>
            <select
              className="form-control"
              value={selectedRankForPermissions?.id || ''}
              onChange={(e) => {
                const rank = ranks.find(r => r.id === parseInt(e.target.value));
                if (rank) handleSelectRankForPermissions(rank);
              }}
              style={{ maxWidth: '300px' }}
            >
              <option value="">Choose a rank...</option>
              {ranks.map(rank => (
                <option key={rank.id} value={rank.id}>
                  {rank.display_name} (Priority: {rank.priority})
                </option>
              ))}
            </select>
          </div>

          {selectedRankForPermissions && (
            <div>
              <h3 className="card-title">
                Permissions for {selectedRankForPermissions.display_name}
              </h3>
              
              {permissions.length > 0 ? (
                Object.entries(
                  permissions.reduce((groups, permission) => {
                    const category = permission.category || 'general';
                    if (!groups[category]) groups[category] = [];
                    groups[category].push(permission);
                    return groups;
                  }, {})
                ).map(([category, categoryPermissions]) => (
                  <div key={category} className="permission-category" style={{ marginBottom: '20px' }}>
                    <h4 style={{ 
                      textTransform: 'capitalize', 
                      borderBottom: '1px solid #ddd', 
                      paddingBottom: '5px',
                      marginBottom: '10px' 
                    }}>
                      {category} Permissions
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '10px' }}>
                      {categoryPermissions.map(permission => {
                        const rankPermission = rankPermissions.find(rp => rp.id === permission.id);
                        return (
                          <label key={permission.id} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            padding: '8px', 
                            border: '1px solid #ddd', 
                            borderRadius: '4px',
                            cursor: 'pointer',
                            backgroundColor: rankPermission?.has_permission ? '#f0f8ff' : '#fff'
                          }}>
                            <input
                              type="checkbox"
                              checked={rankPermission?.has_permission || false}
                              onChange={() => handlePermissionToggle(permission.id)}
                              style={{ marginRight: '8px' }}
                            />
                            <div>
                              <strong>{permission.display_name}</strong>
                              {permission.description && (
                                <div style={{ fontSize: '0.9em', color: '#666' }}>
                                  {permission.description}
                                </div>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))
              ) : (
                <p>Loading permissions...</p>
              )}

              <div className="form-actions" style={{ marginTop: '20px' }}>
                <button
                  onClick={handleSaveRankPermissions}
                  className="btn btn-primary"
                >
                  Save Permissions
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Violations Management */}
      {(userPermissions.includes('view_all_violations') || userPermissions.includes('view_rule_violations')) && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Schedule Violations</h2>
          </div>
          <div className="form-row">
            {userPermissions.includes('view_all_violations') && (
              <div className="form-group">
                <label className="form-label">Admin Violations View</label>
                <p className="mb-2" style={{ color: '#6b7280', fontSize: '0.9em' }}>
                  View schedule violations for all users across the system
                </p>
                <a href="/admin-schedule-violations" className="btn btn-primary">
                  View All Violations
                </a>
              </div>
            )}
            {userPermissions.includes('view_rule_violations') && (
              <div className="form-group">
                <label className="form-label">Individual Violations View</label>
                <p className="mb-2" style={{ color: '#6b7280', fontSize: '0.9em' }}>
                  View detailed rule violations and scheduling analysis
                </p>
                <a href="/schedule-violations" className="btn btn-secondary">
                  View Rule Violations
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cache Management for Admins */}
      <div className="card">
        <CacheUpdateManager adminMode={true} />
      </div>

      {/* Version Control for Admins */}
      {userPermissions.includes('manage_version_config') && (
        <div className="card">
          <VersionConfigManager />
        </div>
      )}

      {/* Map Management (permission required) */}
      {userPermissions.includes('manage_map_settings') && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">🗺️ Map Management</h2>
            <p className="card-subtitle">Configure beach locations and map settings for the public dashboard</p>
          </div>
          <MapManagement />
        </div>
      )}

      {/* Daily Notifications Settings (permission required) */}
      {userPermissions.includes('manage_daily_notifications') && (
        <div className="card">
          <NotificationSettingsManager />
        </div>
      )}
    </div>
  );
};

export default UserManagement; 